'use client'
import { useState } from 'react'
import { Container, ContentWrapper, Headline, Subheadline } from '@/components/ui'
import { useABTest } from '@/components/analytics/useABTest'
import Image from 'next/image'

interface VideoShowcaseProps {
  videoId?: string
  title?: string
  subtitle?: string
  className?: string
}

const VideoShowcase = ({ 
  videoId = "iTQ2QwJrn-A",
  title = "See Hypertask in Action",
  subtitle = "Watch how teams are transforming their productivity with AI-powered project management",
  className = "VWO_365"
}: VideoShowcaseProps) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const { showStaticImages, isLoading } = useABTest()
  
  const loadVideo = () => {
    setIsVideoLoaded(true)
  }

  return (
    <Container variant="section" className={className}>
      <ContentWrapper gap="lg">
        {/* Section Header */}
        <div className="text-center ">
          <Headline variant="regular" className="text-white mb-4">
            {title}
          </Headline>
          <Subheadline className="text-gray-400 max-w-3xl mx-auto">
            {subtitle}
          </Subheadline>
        </div>

        {/* Video Container */}
        <div className="w-full max-w-5xl mx-auto">
          <div 
            className="relative w-full rounded-xl shadow-2xl overflow-hidden" 
            style={{ 
              paddingBottom: '56.25%', /* 16:9 aspect ratio */
              background: '#18181b'
            }}
          >
            {!isLoading ? (
              // Show static image instead of video
              <Image
                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                alt="Hypertask Demo Screenshot"
                className="absolute top-0 left-0 w-full h-full object-cover"
                fill
                priority
              />
            ) : !isVideoLoaded ? (
              // YouTube thumbnail with play button - no third-party scripts loaded
              <div 
                className="absolute top-0 left-0 w-full h-full cursor-pointer group"
                onClick={loadVideo}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    loadVideo()
                  }
                }}
                aria-label="Play video"
              >
                {/* YouTube thumbnail - loads instantly, no scripts */}
                <img
                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                  alt="Video thumbnail"
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  loading="lazy"
                />
                
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-300" />
                
                {/* Play button */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <svg 
                    className="w-20 h-20 text-white opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" 
                    viewBox="0 0 68 48"
                  >
                    <path 
                      className="fill-red-600" 
                      d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                    />
                    <path d="M 45,24 27,14 27,34" fill="#fff"/>
                  </svg>
                </div>
                
                {/* "Click to play" text */}
                <div className="absolute bottom-4 left-4 text-white text-sm opacity-80">
                  Click to play video
                </div>
              </div>
            ) : (
              // Load actual YouTube iframe only after user clicks
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&color=white&theme=dark&autoplay=1`}
                title="Hypertask Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
        </div>
      </ContentWrapper>
    </Container>
  )
}

export default VideoShowcase
